import Responsive from '@root/core/src/utils/responsive';
import { Colors, StyleSheet } from '@root/core/src/utils/styles';
import { semiBold as rootSansSemiBold } from '@root/brand/src/assets/fonts/root-sans/emotion';

const ROOT_SANS_REGULAR = '"Root Sans 5", "Root Sans", sans-serif';
const ROOT_SANS_BOLD = '"Root Sans 9", "Root Sans", sans-serif';

export default StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100vw',
    minHeight: '100vh',
    marginLeft: 'calc(-50vw + 50%)',
    backgroundColor: Colors.white(),
    fontFamily: ROOT_SANS_REGULAR,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 101,
    width: '100%',
    height: '61px',
    padding: '0 20px',
    boxSizing: 'border-box',
    backgroundColor: Colors.white(),
    ...Responsive.md({
      padding: '0 80px',
    }),
  },
  headerLogo: {
    color: Colors.black(),
    display: 'flex',
    alignItems: 'center',
  },

  landing: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    width: '100%',
    maxWidth: '390px',
    padding: '88px 24px 40px',
    boxSizing: 'border-box',
    ...Responsive.lessThanMd({
      justifyContent: 'flex-start',
      minHeight: 'auto',
      margin: '0 auto',
    }),
    ...Responsive.md({
      display: 'grid',
      gridTemplateColumns: 'minmax(630px, 1fr) minmax(520px, 1fr)',
      alignItems: 'start',
      columnGap: '34px',
      maxWidth: '1280px',
      padding: '96px 48px 80px',
    }),
  },
  headlineColumn: {
    width: '100%',
    ...Responsive.md({
      paddingTop: '10px',
    }),
  },
  headline: {
    margin: 0,
    color: Colors.nearBlack(),
    ...rootSansSemiBold(),
    fontSize: '30px',
    lineHeight: '1.18',
    letterSpacing: 0,
    ...Responsive.md({
      maxWidth: '630px',
      fontSize: '56px',
      lineHeight: '1.05',
    }),
  },
  headlineRegisteredMark: {
    fontSize: '0.65em',
    lineHeight: 0,
  },

  actionColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    ...Responsive.md({
      gap: '32px',
    }),
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    margin: '0 -24px',
    padding: '24px',
    borderTop: `1px solid ${Colors.gray30()}`,
    ':first-of-type': {
      borderTop: 'none',
      paddingTop: 0,
    },
    ':last-of-type': {
      paddingBottom: 0,
    },
    ...Responsive.md({
      margin: 0,
      padding: '28px 0',
      gap: '28px',
      ':first-of-type': {
        borderTop: 'none',
        paddingTop: 0,
      },
      ':last-of-type': {
        paddingBottom: 0,
      },
    }),
  },
  stepText: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  stepTitleRow: {
    display: 'grid',
    gridTemplateColumns: '10px minmax(0, 1fr)',
    columnGap: '12px',
    alignItems: 'start',
    marginBottom: '8px',
    ...Responsive.md({
      gridTemplateColumns: '18px minmax(0, 1fr)',
      columnGap: '16px',
    }),
  },
  stepNumber: {
    color: Colors.black(),
    ...rootSansSemiBold(),
    fontSize: '20px',
    lineHeight: '1.2',
    letterSpacing: 0,
  },
  stepTitle: {
    margin: 0,
    color: Colors.black(),
    ...rootSansSemiBold(),
    fontSize: '20px',
    lineHeight: '1.2',
    letterSpacing: 0,
    ...Responsive.md({
      fontSize: '24px',
    }),
  },
  stepRegisteredMark: {
    fontSize: '0.65em',
    lineHeight: 0,
  },
  stepDescription: {
    margin: 0,
    paddingLeft: '22px',
    color: Colors.gray50(),
    fontFamily: ROOT_SANS_REGULAR,
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '1.3',
    letterSpacing: 0,
    ...Responsive.md({
      maxWidth: '390px',
      paddingLeft: '34px',
      fontSize: '18px',
    }),
  },
  stepImage: {
    flexShrink: 0,
    objectFit: 'contain',
  },
  appBadgeImage: {
    width: '72px',
    height: '65px',
    objectFit: 'cover',
    ...Responsive.md({
      width: '132px',
      height: '119px',
    }),
  },
  carImage: {
    width: '87px',
    height: '57px',
    objectFit: 'cover',
    ...Responsive.md({
      width: '168px',
      height: '110px',
    }),
  },
  graphImage: {
    width: '75px',
    height: '96px',
    objectFit: 'cover',
    ...Responsive.md({
      width: '138px',
      height: '176px',
    }),
  },

  ctaButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '60px',
    padding: '16px 20px',
    backgroundColor: Colors.rootOrange(),
    border: 'none',
    borderRadius: '6px',
    color: Colors.white(),
    cursor: 'pointer',
    fontFamily: ROOT_SANS_BOLD,
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: '1.3',
    letterSpacing: 0,
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.75,
    },
  },
  buttonRegisteredMark: {
    fontSize: '0.65em',
    lineHeight: 0,
  },
  consentText: {
    margin: 0,
    color: Colors.gray50(),
    fontFamily: ROOT_SANS_REGULAR,
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '1.3',
    letterSpacing: 0,
    ...Responsive.md({
      maxWidth: '520px',
    }),
  },
  consentRegisteredMark: {
    fontSize: '0.65em',
    lineHeight: 0,
  },
  consentLink: {
    color: Colors.rootOrange(),
    textDecoration: 'underline',
  },
});
